import { getCountryOptions } from '~/types/Address';
import { CarrierFormField } from '~/types/CarrierFormField';
import { CarrierFormType } from '../CarrierForm/CarrierForm';

const upsEditParameters: CarrierFormField[] = [
  {
    rank: 5,
    key: 'account_number',
    label: 'UPS Account Number',
    placeholderText: null,
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 6,
    isRequired: true,
    helpUrl: null,
    disabled: true,
  },
  {
    rank: 10,
    key: 'cost_center',
    label: 'Mail Innovations Cost Center',
    placeholderText: null,
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 20, // arbitrary number, online examples seem to be length of 8
    isRequired: false,
    helpUrl: null,
    tooltip: 'An identifier that UPS Mail Innovations assigned to your account.',
  },
  {
    rank: 15,
    key: 'usps_endorsement',
    label: 'USPS Endorsement',
    placeholderText: null,
    fieldType: 'choice',
    displayType: 'show',
    minLength: null,
    maxLength: null,
    isRequired: false,
    helpUrl: null,
    options: [
      { value: 1, label: 'Return Service Requested (1)' },
      { value: 2, label: 'Forwarding Service Requested (2)' },
      { value: 3, label: 'Address Service Requested (3)' },
      { value: 4, label: 'Change Service Requested (4)' },
      { value: 5, label: 'No Service Selected (5)' },
    ],
    hiddenByKey: 'cost_center',
    hiddenByValue: undefined,
    tooltip:
      'Specify how USPS, the last-mile carrier for UPS Mail Innovations, should handle non-deliverable packages.',
  },
  {
    rank: 20,
    key: 'surepost',
    label: 'Activate UPS Surepost',
    placeholderText: null,
    fieldType: 'boolean',
    displayType: 'show',
    minLength: null,
    maxLength: null,
    isRequired: false,
    helpUrl: null,
    tooltip:
      'Activate this field if your account supports UPS Surepost. This needs to be explicitly enabled by UPS.',
  },
];

const canadaPostCreateParameters: CarrierFormField[] = [
  {
    rank: 10,
    key: 'street_number',
    label: 'Street #',
    placeholderText: null,
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 10,
    isRequired: true,
    helpUrl: null,
  },
  {
    rank: 20,
    key: 'street',
    label: 'Street',
    placeholderText: null,
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 60,
    isRequired: true,
    helpUrl: null,
  },
  {
    rank: 30,
    key: 'unit_suite_apt',
    label: 'Unit',
    placeholderText: null,
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 10,
    isRequired: false,
    helpUrl: null,
  },
  {
    rank: 40,
    key: 'city',
    label: 'City',
    placeholderText: null,
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 40,
    isRequired: true,
    helpUrl: null,
  },
  {
    rank: 50,
    key: 'prov_state',
    label: 'State / Province',
    placeholderText: 'Enter a 2-letter state code',
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 2,
    isRequired: true,
    helpUrl: null,
  },
  {
    rank: 60,
    key: 'postal_zip_code',
    label: 'Zip / Postal Code',
    placeholderText: null,
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 10,
    isRequired: true,
    helpUrl: null,
  },
  {
    rank: 70,
    key: 'country_code',
    label: 'Country',
    placeholderText: null,
    fieldType: 'choice',
    displayType: 'show',
    minLength: null,
    maxLength: null,
    isRequired: true,
    helpUrl: null,
    options: [
      { value: 'CA', label: 'Canada' },
      { value: 'US', label: 'United States' },
    ],
  },
  {
    rank: 80,
    key: 'phone_number',
    label: 'Phone',
    placeholderText: null,
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 30,
    isRequired: true,
    helpUrl: null,
  },
  {
    rank: 90,
    key: 'legal_name',
    label: 'Business Name',
    placeholderText: null,
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 40,
    isRequired: true,
    helpUrl: null,
  },
  {
    rank: 100,
    key: 'canada_parcel_volume',
    label: 'How many items do you ship monthly to Canada?',
    placeholderText: null,
    fieldType: 'integer',
    displayType: 'show',
    minLength: null,
    maxLength: 7,
    isRequired: false,
    helpUrl: null,
  },
  {
    rank: 110,
    key: 'us_parcel_volume',
    label: 'How many items do you ship monthly to United States?',
    placeholderText: null,
    fieldType: 'integer',
    displayType: 'show',
    minLength: null,
    maxLength: 7,
    isRequired: false,
    helpUrl: null,
  },
  {
    rank: 120,
    key: 'international_parcel_volume',
    label: 'How many items do you ship monthly to international countries?',
    placeholderText: null,
    fieldType: 'integer',
    displayType: 'show',
    minLength: null,
    maxLength: 7,
    isRequired: false,
    helpUrl: null,
  },
  {
    rank: 150,
    key: 'canada_post_agreement',
    label:
      'By checking this box, you agree to the <a href="https://goshippo.com/partners_static/canada_post/terms/">Canada Post Shipping Service Agreement</a>',
    placeholderText: null,
    fieldType: 'boolean',
    displayType: 'show',
    minLength: null,
    maxLength: null,
    isRequired: true,
    helpUrl: null,
    defaultValue: true,
  },
];

export const eBayCanadaPostConnectParameters: CarrierFormField[] = [
  {
    rank: 20,
    key: 'customer_number',
    label: 'Canada Post Customer Number',
    placeholderText: null,
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 10,
    isRequired: true,
    helpUrl: null,
  },
  {
    rank: 40,
    key: 'postal_zip_code',
    label: 'Zip / Postal Code',
    placeholderText: null,
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 10,
    isRequired: true,
    helpUrl: null,
  },
];

const canadaPostParameters: CarrierFormField[] = [
  {
    rank: 20,
    key: 'customer_number',
    label: 'Canada Post Customer Number',
    placeholderText: null,
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 10,
    isRequired: true,
    helpUrl: null,
  },
  {
    rank: 40,
    key: 'account_id',
    label: 'API Username',
    placeholderText: null,
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 16,
    isRequired: true,
    helpUrl:
      'https://support.goshippo.com/hc/en-us/articles/201772755-Where-do-I-get-my-Canada-Post-API-username-and-password-',
  },
  {
    rank: 60,
    key: 'api_password',
    label: 'API Password',
    placeholderText: null,
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 22,
    isRequired: true,
    helpUrl: null,
  },
  {
    rank: 80,
    key: 'contract_id',
    label: 'Contract ID (optional)',
    placeholderText: null,
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 10,
    isRequired: false,
    helpUrl: null,
  },
  {
    rank: 100,
    key: 'payment_method',
    label: 'Payment Method',
    placeholderText: null,
    fieldType: 'choice',
    displayType: 'show',
    minLength: null,
    maxLength: null,
    isRequired: true,
    helpUrl: null,
    options: [
      { value: 'CreditCard', label: 'Credit Card' },
      { value: 'Account', label: 'Bill Account' },
    ],
    defaultValue: 'CreditCard',
  },
  {
    rank: 120,
    key: 'use_manifests',
    label: 'Use Manifests',
    placeholderText: null,
    fieldType: 'boolean',
    displayType: 'show',
    minLength: null,
    maxLength: null,
    isRequired: false,
    helpUrl: null,
  },
];

const lasershipParameters: CarrierFormField[] = [
  {
    rank: 20,
    key: 'account_id',
    label: 'Lasership API Key',
    placeholderText: null,
    fieldType: 'string',
    displayType: 'mask',
    minLength: null,
    maxLength: 255,
    isRequired: true,
    helpUrl: null,
  },
  {
    rank: 40,
    key: 'lasership_apiid',
    label: 'API ID',
    placeholderText: null,
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 255,
    isRequired: true,
    helpUrl: null,
  },
  {
    rank: 60,
    key: 'critical_pull_time',
    label: 'Critical Pull Time',
    placeholderText: 'HH:MM 24-hour time format',
    fieldType: 'military_time',
    displayType: 'show',
    minLength: null,
    maxLength: null,
    isRequired: true,
    helpUrl: null,
  },
  {
    rank: 80,
    key: 'default_customer_branch',
    label: 'Default Customer Branch',
    placeholderText: null,
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 255,
    isRequired: false,
    helpUrl: null,
  },
];

const dhlExpressParameters: CarrierFormField[] = [
  {
    rank: 20,
    key: 'account_id',
    label: 'DHL Express Account Number',
    placeholderText: null,
    fieldType: 'string',
    displayType: 'show_partial',
    minLength: null,
    maxLength: 10,
    isRequired: true,
    helpUrl: null,
  },
  {
    rank: 30,
    key: 'site_id',
    label: 'Site ID',
    placeholderText: null,
    fieldType: 'string',
    displayType: 'show_partial',
    minLength: 6,
    maxLength: 20,
    isRequired: true,
    helpUrl: 'https://support.goshippo.com/hc/en-us/articles/4409158652571',
  },
  {
    rank: 40,
    key: 'password',
    label: 'Password',
    placeholderText: null,
    fieldType: 'string',
    displayType: 'mask',
    minLength: 8,
    maxLength: 20,
    isRequired: true,
    helpUrl: null,
  },
  {
    rank: 60,
    key: 'payment_country',
    label: 'Registered Country',
    placeholderText: null,
    fieldType: 'choice',
    displayType: 'show',
    minLength: null,
    maxLength: null,
    isRequired: true,
    helpUrl: null,
    options: getCountryOptions(),
  },
];

export const AUSPOST_MYPOST_OPTIONS = [
  {
    value: 'is_mypost',
    label: 'MyPost (under 1,000 labels per year)',
  },
  {
    value: 'is_eparcel',
    label: 'eParcel (1,000+ labels a year)',
  },
];

const UPS_REGISTRATION_COUNTRIES = [
  {
    value: 'AR',
    label: 'Argentina',
  },
  {
    value: 'AU',
    label: 'Australia',
  },
  {
    value: 'AT',
    label: 'Austria',
  },
  {
    value: 'BE',
    label: 'Belgium',
  },
  {
    value: 'BR',
    label: 'Brazil',
  },
  {
    value: 'CA',
    label: 'Canada',
  },
  {
    value: 'CL',
    label: 'Chile',
  },
  {
    value: 'CN',
    label: 'China',
  },
  {
    value: 'CR',
    label: 'Costa Rica',
  },
  {
    value: 'CZ',
    label: 'Czech Republic',
  },
  {
    value: 'DK',
    label: 'Denmark',
  },
  {
    value: 'DO',
    label: 'Dominican Republic',
  },
  {
    value: 'EC',
    label: 'Ecuador',
  },
  {
    value: 'FI',
    label: 'Finland',
  },
  {
    value: 'FR',
    label: 'France',
  },
  {
    value: 'DE',
    label: 'Germany',
  },
  {
    value: 'GR',
    label: 'Greece',
  },
  {
    value: 'GT',
    label: 'Guatemala',
  },
  {
    value: 'GG',
    label: 'Guernsey',
  },
  {
    value: 'HK',
    label: 'Hong Kong',
  },
  {
    value: 'HU',
    label: 'Hungary',
  },
  {
    value: 'IN',
    label: 'India',
  },
  {
    value: 'ID',
    label: 'Indonesia',
  },
  {
    value: 'IE',
    label: 'Ireland',
  },
  {
    value: 'IL',
    label: 'Israel',
  },
  {
    value: 'IT',
    label: 'Italy',
  },
  {
    value: 'JP',
    label: 'Japan',
  },
  {
    value: 'JE',
    label: 'Jersey',
  },
  {
    value: 'KR',
    label: 'Korea, Republic of',
  },
  {
    value: 'MO',
    label: 'Macao',
  },
  {
    value: 'MY',
    label: 'Malaysia',
  },
  {
    value: 'MX',
    label: 'Mexico',
  },
  {
    value: 'NL',
    label: 'Netherlands',
  },
  {
    value: 'NZ',
    label: 'New Zealand',
  },
  {
    value: 'NO',
    label: 'Norway',
  },
  {
    value: 'PA',
    label: 'Panama',
  },
  {
    value: 'PH',
    label: 'Philippines',
  },
  {
    value: 'PL',
    label: 'Poland',
  },
  {
    value: 'PT',
    label: 'Portugal',
  },
  {
    value: 'PR',
    label: 'Puerto Rico',
  },
  {
    value: 'RO',
    label: 'Romania',
  },
  {
    value: 'RU',
    label: 'Russian Federation',
  },
  {
    value: 'SG',
    label: 'Singapore',
  },
  {
    value: 'ES',
    label: 'Spain',
  },
  {
    value: 'SE',
    label: 'Sweden',
  },
  {
    value: 'CH',
    label: 'Switzerland',
  },
  {
    value: 'TW',
    label: 'Taiwan, Province of China',
  },
  {
    value: 'TH',
    label: 'Thailand',
  },
  {
    value: 'TR',
    label: 'Turkey',
  },
  {
    value: 'AE',
    label: 'United Arab Emirates',
  },
  {
    value: 'GB',
    label: 'United Kingdom',
  },
  {
    value: 'US',
    label: 'United States',
  },
  {
    value: 'VN',
    label: 'Viet Nam',
  },
];

const upsConnectParameters: CarrierFormField[] = [
  {
    rank: 10,
    key: 'full_name',
    label: 'Full Name',
    placeholderText: null,
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 20,
    isRequired: true,
    helpUrl: null,
  },
  {
    rank: 15,
    key: 'title',
    label: 'Title',
    placeholderText: 'Your title (e.g. Doctor, Artist, Manager, ...)',
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 20,
    isRequired: true,
    helpUrl: null,
  },
  {
    rank: 20,
    key: 'company',
    label: 'Company',
    placeholderText: "If you don't have a company please fill with your full name",
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 30,
    isRequired: true,
    helpUrl: null,
  },
  {
    rank: 25,
    key: 'email',
    label: 'Email',
    placeholderText: null,
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 50,
    isRequired: true,
    helpUrl: null,
  },
  {
    rank: 30,
    key: 'phone',
    label: 'Phone',
    placeholderText: null,
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 30,
    isRequired: true,
    helpUrl: null,
  },
  {
    rank: 35,
    key: 'billing_address_street1',
    label: 'Street (1)',
    placeholderText: null,
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 30,
    isRequired: true,
    helpUrl: null,
  },
  {
    rank: 40,
    key: 'billing_address_street2',
    label: 'Street (2)',
    placeholderText: 'Apartment, suit, unit, floor, etc.',
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 50,
    isRequired: false,
    helpUrl: null,
  },
  {
    rank: 50,
    key: 'billing_address_city',
    label: 'City',
    placeholderText: null,
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 30,
    isRequired: true,
    helpUrl: null,
  },
  {
    rank: 55,
    key: 'billing_address_state',
    label: 'State (required for USA and Canada)',
    placeholderText: 'Use official code for other countries if applicable',
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 20,
    isRequired: false,
    helpUrl: null,
  },
  {
    rank: 60,
    key: 'billing_address_zip',
    label: 'Zip / Postal Code',
    placeholderText: null,
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 10,
    isRequired: true,
    helpUrl: null,
  },
  {
    rank: 65,
    key: 'billing_address_country_iso2',
    label: 'Country',
    placeholderText: null,
    fieldType: 'choice',
    displayType: 'show',
    minLength: null,
    maxLength: null,
    isRequired: true,
    helpUrl: null,
    options: UPS_REGISTRATION_COUNTRIES,
  },
  {
    rank: 70,
    key: 'account_number',
    label: 'UPS Account Number',
    placeholderText: null,
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 20,
    isRequired: true,
    helpUrl: null,
  },
  {
    rank: 80,
    key: 'collec_zip',
    label: 'Postcode (collection location)',
    placeholderText: null,
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 10,
    isRequired: true,
    helpUrl: null,
  },
  {
    rank: 85,
    key: 'collec_country_iso2',
    label: 'Country',
    placeholderText: null,
    fieldType: 'choice',
    displayType: 'show',
    minLength: null,
    maxLength: null,
    isRequired: true,
    helpUrl: null,
    options: UPS_REGISTRATION_COUNTRIES,
  },
  {
    rank: 90,
    key: 'has_invoice',
    label:
      '<b>Show personalized rates (requires latest invoice).</b><br />If disabled, you\'ll only see retail rates. <a style="color: #226015" href="https://support.goshippo.com/hc/en-us/articles/204781715-How-to-Connect-Your-UPS-Account-in-Shippo#h_877e2e40-64ad-4051-95fc-ea3b5afe6a4a">Learn more</a>',
    placeholderText: null,
    fieldType: 'boolean',
    displayType: 'show',
    minLength: null,
    maxLength: null,
    isRequired: false,
    helpUrl: null,
    defaultValue: false,
  },
  {
    rank: 95,
    key: 'invoice_number',
    label: 'Invoice Number',
    placeholderText: 'Invoice number of a Regular account',
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 30,
    isRequired: true,
    helpUrl: null,
    hiddenByKey: 'has_invoice',
    hiddenByValue: false,
  },
  {
    rank: 100,
    key: 'currency_code',
    label: 'Currency Code',
    placeholderText: null,
    fieldType: 'choice',
    displayType: 'show',
    minLength: null,
    maxLength: null,
    isRequired: true,
    helpUrl: null,
    options: [
      {
        value: 'AED',
        label: 'AED - United Arab Emirates Dirham',
      },
      {
        value: 'AFN',
        label: 'AFN - Afghan Afghani',
      },
      {
        value: 'ALL',
        label: 'ALL - Albanian Lek',
      },
      {
        value: 'AMD',
        label: 'AMD - Armenian Dram',
      },
      {
        value: 'ANG',
        label: 'ANG - Netherlands Antillean Guilder',
      },
      {
        value: 'AOA',
        label: 'AOA - Angolan Kwanza',
      },
      {
        value: 'ARS',
        label: 'ARS - Argentine Peso',
      },
      {
        value: 'AUD',
        label: 'AUD - Australian Dollar',
      },
      {
        value: 'AWG',
        label: 'AWG - Aruban Florin',
      },
      {
        value: 'AZN',
        label: 'AZN - Azerbaijani Manat',
      },
      {
        value: 'BAM',
        label: 'BAM - Bosnia-Herzegovina Convertible Mark',
      },
      {
        value: 'BBD',
        label: 'BBD - Barbadian Dollar',
      },
      {
        value: 'BDT',
        label: 'BDT - Bangladeshi Taka',
      },
      {
        value: 'BGN',
        label: 'BGN - Bulgarian Lev',
      },
      {
        value: 'BHD',
        label: 'BHD - Bahraini Dinar',
      },
      {
        value: 'BIF',
        label: 'BIF - Burundian Franc',
      },
      {
        value: 'BMD',
        label: 'BMD - Bermudan Dollar',
      },
      {
        value: 'BND',
        label: 'BND - Brunei Dollar',
      },
      {
        value: 'BOB',
        label: 'BOB - Bolivian Boliviano',
      },
      {
        value: 'BRL',
        label: 'BRL - Brazilian Real',
      },
      {
        value: 'BSD',
        label: 'BSD - Bahamian Dollar',
      },
      {
        value: 'BTN',
        label: 'BTN - Bhutanese Ngultrum',
      },
      {
        value: 'BWP',
        label: 'BWP - Botswanan Pula',
      },
      {
        value: 'BYR',
        label: 'BYR - Belarusian Ruble',
      },
      {
        value: 'BZD',
        label: 'BZD - Belize Dollar',
      },
      {
        value: 'CAD',
        label: 'CAD - Canadian Dollar',
      },
      {
        value: 'CDF',
        label: 'CDF - Congolese Franc',
      },
      {
        value: 'CHF',
        label: 'CHF - Swiss Franc',
      },
      {
        value: 'CLF',
        label: 'CLF - Chilean Unit of Account (UF)',
      },
      {
        value: 'CLP',
        label: 'CLP - Chilean Peso',
      },
      {
        value: 'COP',
        label: 'COP - Colombian Peso',
      },
      {
        value: 'CRC',
        label: 'CRC - Costa Rican Colón',
      },
      {
        value: 'CUP',
        label: 'CUP - Cuban Peso',
      },
      {
        value: 'CVE',
        label: 'CVE - Cape Verdean Escudo',
      },
      {
        value: 'CZK',
        label: 'CZK - Czech Republic Koruna',
      },
      {
        value: 'DJF',
        label: 'DJF - Djiboutian Franc',
      },
      {
        value: 'DKK',
        label: 'DKK - Danish Krone',
      },
      {
        value: 'DOP',
        label: 'DOP - Dominican Peso',
      },
      {
        value: 'DZD',
        label: 'DZD - Algerian Dinar',
      },
      {
        value: 'EGP',
        label: 'EGP - Egyptian Pound',
      },
      {
        value: 'ERN',
        label: 'ERN - Eritrean Nakfa',
      },
      {
        value: 'ETB',
        label: 'ETB - Ethiopian Birr',
      },
      {
        value: 'EUR',
        label: 'EUR - Euro',
      },
      {
        value: 'FJD',
        label: 'FJD - Fijian Dollar',
      },
      {
        value: 'FKP',
        label: 'FKP - Falkland Islands Pound',
      },
      {
        value: 'GBP',
        label: 'GBP - British Pound Sterling',
      },
      {
        value: 'GEL',
        label: 'GEL - Georgian Lari',
      },
      {
        value: 'GHS',
        label: 'GHS - Ghanaian Cedi',
      },
      {
        value: 'GIP',
        label: 'GIP - Gibraltar Pound',
      },
      {
        value: 'GMD',
        label: 'GMD - Gambian Dalasi',
      },
      {
        value: 'GNF',
        label: 'GNF - Guinean Franc',
      },
      {
        value: 'GTQ',
        label: 'GTQ - Guatemalan Quetzal',
      },
      {
        value: 'GYD',
        label: 'GYD - Guyanaese Dollar',
      },
      {
        value: 'HKD',
        label: 'HKD - Hong Kong Dollar',
      },
      {
        value: 'HNL',
        label: 'HNL - Honduran Lempira',
      },
      {
        value: 'HRK',
        label: 'HRK - Croatian Kuna',
      },
      {
        value: 'HTG',
        label: 'HTG - Haitian Gourde',
      },
      {
        value: 'HUF',
        label: 'HUF - Hungarian Forint',
      },
      {
        value: 'IDR',
        label: 'IDR - Indonesian Rupiah',
      },
      {
        value: 'ILS',
        label: 'ILS - Israeli New Sheqel',
      },
      {
        value: 'INR',
        label: 'INR - Indian Rupee',
      },
      {
        value: 'IQD',
        label: 'IQD - Iraqi Dinar',
      },
      {
        value: 'IRR',
        label: 'IRR - Iranian Rial',
      },
      {
        value: 'ISK',
        label: 'ISK - Icelandic Króna',
      },
      {
        value: 'JMD',
        label: 'JMD - Jamaican Dollar',
      },
      {
        value: 'JOD',
        label: 'JOD - Jordanian Dinar',
      },
      {
        value: 'JPY',
        label: 'JPY - Japanese Yen',
      },
      {
        value: 'KES',
        label: 'KES - Kenyan Shilling',
      },
      {
        value: 'KGS',
        label: 'KGS - Kyrgystani Som',
      },
      {
        value: 'KHR',
        label: 'KHR - Cambodian Riel',
      },
      {
        value: 'KMF',
        label: 'KMF - Comorian Franc',
      },
      {
        value: 'KPW',
        label: 'KPW - North Korean Won',
      },
      {
        value: 'KRW',
        label: 'KRW - South Korean Won',
      },
      {
        value: 'KWD',
        label: 'KWD - Kuwaiti Dinar',
      },
      {
        value: 'KYD',
        label: 'KYD - Cayman Islands Dollar',
      },
      {
        value: 'KZT',
        label: 'KZT - Kazakhstani Tenge',
      },
      {
        value: 'LAK',
        label: 'LAK - Laotian Kip',
      },
      {
        value: 'LBP',
        label: 'LBP - Lebanese Pound',
      },
      {
        value: 'LKR',
        label: 'LKR - Sri Lankan Rupee',
      },
      {
        value: 'LRD',
        label: 'LRD - Liberian Dollar',
      },
      {
        value: 'LSL',
        label: 'LSL - Lesotho Loti',
      },
      {
        value: 'LYD',
        label: 'LYD - Libyan Dinar',
      },
      {
        value: 'MAD',
        label: 'MAD - Moroccan Dirham',
      },
      {
        value: 'MDL',
        label: 'MDL - Moldovan Leu',
      },
      {
        value: 'MGA',
        label: 'MGA - Malagasy Ariary',
      },
      {
        value: 'MKD',
        label: 'MKD - Macedonian Denar',
      },
      {
        value: 'MMK',
        label: 'MMK - Myanma Kyat',
      },
      {
        value: 'MNT',
        label: 'MNT - Mongolian Tugrik',
      },
      {
        value: 'MOP',
        label: 'MOP - Macanese Pataca',
      },
      {
        value: 'MRO',
        label: 'MRO - Mauritanian Ouguiya',
      },
      {
        value: 'MUR',
        label: 'MUR - Mauritian Rupee',
      },
      {
        value: 'MVR',
        label: 'MVR - Maldivian Rufiyaa',
      },
      {
        value: 'MWK',
        label: 'MWK - Malawian Kwacha',
      },
      {
        value: 'MXN',
        label: 'MXN - Mexican Peso',
      },
      {
        value: 'MYR',
        label: 'MYR - Malaysian Ringgit',
      },
      {
        value: 'MZN',
        label: 'MZN - Mozambican Metical',
      },
      {
        value: 'NAD',
        label: 'NAD - Namibian Dollar',
      },
      {
        value: 'NGN',
        label: 'NGN - Nigerian Naira',
      },
      {
        value: 'NIO',
        label: 'NIO - Nicaraguan Córdoba',
      },
      {
        value: 'NOK',
        label: 'NOK - Norwegian Krone',
      },
      {
        value: 'NPR',
        label: 'NPR - Nepalese Rupee',
      },
      {
        value: 'NZD',
        label: 'NZD - New Zealand Dollar',
      },
      {
        value: 'OMR',
        label: 'OMR - Omani Rial',
      },
      {
        value: 'PAB',
        label: 'PAB - Panamanian Balboa',
      },
      {
        value: 'PEN',
        label: 'PEN - Peruvian Nuevo Sol',
      },
      {
        value: 'PGK',
        label: 'PGK - Papua New Guinean Kina',
      },
      {
        value: 'PHP',
        label: 'PHP - Philippine Peso',
      },
      {
        value: 'PKR',
        label: 'PKR - Pakistani Rupee',
      },
      {
        value: 'PLN',
        label: 'PLN - Polish Zloty',
      },
      {
        value: 'PYG',
        label: 'PYG - Paraguayan Guarani',
      },
      {
        value: 'QAR',
        label: 'QAR - Qatari Rial',
      },
      {
        value: 'RMB',
        label: 'RMB - Chinese Yuan Renminbi',
      },
      {
        value: 'RON',
        label: 'RON - Romanian Leu',
      },
      {
        value: 'RSD',
        label: 'RSD - Serbian Dinar',
      },
      {
        value: 'RUB',
        label: 'RUB - Russian Ruble',
      },
      {
        value: 'RWF',
        label: 'RWF - Rwandan Franc',
      },
      {
        value: 'SAR',
        label: 'SAR - Saudi Riyal',
      },
      {
        value: 'SBD',
        label: 'SBD - Solomon Islands Dollar',
      },
      {
        value: 'SCR',
        label: 'SCR - Seychellois Rupee',
      },
      {
        value: 'SEK',
        label: 'SEK - Swedish Krona',
      },
      {
        value: 'SGD',
        label: 'SGD - Singapore Dollar',
      },
      {
        value: 'SHP',
        label: 'SHP - Saint Helena Pound',
      },
      {
        value: 'SLL',
        label: 'SLL - Sierra Leonean Leone',
      },
      {
        value: 'SOS',
        label: 'SOS - Somali Shilling',
      },
      {
        value: 'SRD',
        label: 'SRD - Surinamese Dollar',
      },
      {
        value: 'STD',
        label: 'STD - São Tomé and Príncipe Dobra',
      },
      {
        value: 'SVC',
        label: 'SVC - Salvadoran Colón',
      },
      {
        value: 'SYP',
        label: 'SYP - Syrian Pound',
      },
      {
        value: 'SZL',
        label: 'SZL - Swazi Lilangeni',
      },
      {
        value: 'THB',
        label: 'THB - Thai Baht',
      },
      {
        value: 'TJS',
        label: 'TJS - Tajikistani Somoni',
      },
      {
        value: 'TND',
        label: 'TND - Tunisian Dinar',
      },
      {
        value: 'TOP',
        label: 'TOP - Tongan Pa´anga',
      },
      {
        value: 'TRY',
        label: 'TRY - Turkish Lira',
      },
      {
        value: 'TTD',
        label: 'TTD - Trinidad and Tobago Dollar',
      },
      {
        value: 'TWD',
        label: 'TWD - New Taiwan Dollar',
      },
      {
        value: 'TZS',
        label: 'TZS - Tanzanian Shilling',
      },
      {
        value: 'UAH',
        label: 'UAH - Ukrainian Hryvnia',
      },
      {
        value: 'UGX',
        label: 'UGX - Ugandan Shilling',
      },
      {
        value: 'USD',
        label: 'USD - United States Dollar',
      },
      {
        value: 'UYU',
        label: 'UYU - Uruguayan Peso',
      },
      {
        value: 'UZS',
        label: 'UZS - Uzbekistan Som',
      },
      {
        value: 'VND',
        label: 'VND - Vietnamese Dong',
      },
      {
        value: 'VUV',
        label: 'VUV - Vanuatu Vatu',
      },
      {
        value: 'WST',
        label: 'WST - Samoan Tala',
      },
      {
        value: 'XAF',
        label: 'XAF - CFA Franc BEAC',
      },
      {
        value: 'XCD',
        label: 'XCD - East Caribbean Dollar',
      },
      {
        value: 'XOF',
        label: 'XOF - CFA Franc BCEAO',
      },
      {
        value: 'XPF',
        label: 'XPF - CFP Franc',
      },
      {
        value: 'YER',
        label: 'YER - Yemeni Rial',
      },
      {
        value: 'ZAR',
        label: 'ZAR - South African Rand',
      },
      {
        value: 'ZMK',
        label: 'ZMK - Zambian Kwacha (pre-2013)',
      },
    ],
    hiddenByKey: 'has_invoice',
    hiddenByValue: false,
  },
  {
    rank: 105,
    key: 'invoice_value',
    label: 'Charges This Period',
    placeholderText: null,
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 30,
    isRequired: true,
    helpUrl: null,
    hiddenByKey: 'has_invoice',
    hiddenByValue: false,
    tooltip: 'Value of the invoice. Max of 16 digits before decimal and 2 digits after decimal.',
  },
  {
    rank: 110,
    key: 'aia_country_iso2',
    label: 'Country',
    placeholderText: 'Country associated with the account that issued the invoice',
    fieldType: 'choice',
    displayType: 'show',
    minLength: null,
    maxLength: 30,
    isRequired: true,
    helpUrl: null,
    options: UPS_REGISTRATION_COUNTRIES,
    hiddenByKey: 'has_invoice',
    hiddenByValue: false,
  },
  {
    rank: 115,
    key: 'invoice_date',
    label: 'Invoice Date',
    placeholderText: null,
    fieldType: 'date',
    displayType: 'show',
    minLength: null,
    maxLength: null,
    isRequired: true,
    helpUrl: null,
    hiddenByKey: 'has_invoice',
    hiddenByValue: false,
  },
  {
    rank: 120,
    key: 'invoice_controlid',
    label: 'Control ID (required for USA)',
    placeholderText: null,
    fieldType: 'string',
    displayType: 'show',
    minLength: null,
    maxLength: 30,
    isRequired: false,
    helpUrl: null,
    hiddenByKey: 'has_invoice',
    hiddenByValue: false,
  },
  {
    rank: 125,
    key: 'ups_agreements',
    label:
      'By checking this box, you agree to the <a style="color: #226015" href="https://goshippo.com/external_registration/ups/technology_agreement">UPS Technology Agreement</a>',
    placeholderText: null,
    fieldType: 'boolean',
    displayType: 'show',
    minLength: null,
    maxLength: null,
    isRequired: true,
    helpUrl: null,
  },
];

type ParameterMap = {
  [carrier: string]: { [key in CarrierFormType]: Maybe<CarrierFormField[]> };
};

export const parametersMap: ParameterMap = {
  ups: {
    create: null, // The UPS Master create form is DB-backed, rest are mocked
    edit: upsEditParameters,
    connect: upsConnectParameters,
  },
  canada_post: {
    create: canadaPostCreateParameters,
    edit: canadaPostParameters,
    connect: canadaPostParameters,
  },
  lasership: {
    create: lasershipParameters,
    edit: lasershipParameters,
    connect: lasershipParameters,
  },
  dhl_express: {
    create: dhlExpressParameters,
    edit: dhlExpressParameters,
    connect: dhlExpressParameters,
  },
};

export const isCarrierFormMocked = (carrier: string, formType: CarrierFormType) => {
  return !!(parametersMap.hasOwnProperty(carrier) && parametersMap[carrier][formType]);
};
